import type { InspectResponse } from "./types";

const llmsBody = `# Example Docs
> Authoritative product documentation, API references, and implementation guides for Example Docs.

## Core resources
- [Getting started](https://docs.example.com/getting-started)
- [API reference](https://docs.example.com/api/reference)
- [SDK guides](https://docs.example.com/sdks)
- [Changelog](https://docs.example.com/changelog)

## Product guides
- [Authentication](https://docs.example.com/guides/authentication)
- [Webhooks](https://docs.example.com/guides/webhooks)
- [Billing](https://docs.example.com/guides/billing)

## Optional
- [Status page](https://status.example.com)`;

const robotsBody = `User-agent: *
Allow: /

User-agent: OAI-SearchBot
Allow: /

User-agent: GPTBot
Disallow: /

User-agent: ClaudeBot
Disallow: /

User-agent: Claude-SearchBot
Allow: /

User-agent: PerplexityBot
Allow: /

User-agent: Google-Extended
Disallow: /

Sitemap: https://docs.example.com/sitemap.xml`;

export const sampleAudit: InspectResponse = {
  input: "https://docs.example.com",
  origin: "https://docs.example.com",
  checkedAt: "2026-06-14T00:00:00.000Z",
  files: [
    {
      kind: "home",
      label: "Homepage headers",
      path: "/",
      url: "https://docs.example.com/",
      status: 200,
      ok: true,
      found: true,
      contentType: "text/html; charset=utf-8",
      bytes: 18452,
      headers: {
        link: '<https://docs.example.com/llms.txt>; rel="llms"',
        "content-type": "text/html; charset=utf-8"
      },
      body: "",
      error: null
    },
    {
      kind: "llms",
      label: "/llms.txt",
      path: "/llms.txt",
      url: "https://docs.example.com/llms.txt",
      status: 200,
      ok: true,
      found: true,
      contentType: "text/plain; charset=utf-8",
      bytes: llmsBody.length,
      headers: { "content-type": "text/plain; charset=utf-8" },
      body: llmsBody,
      error: null
    },
    {
      kind: "wellKnownLlms",
      label: "/.well-known/llms.txt",
      path: "/.well-known/llms.txt",
      url: "https://docs.example.com/.well-known/llms.txt",
      status: 404,
      ok: false,
      found: false,
      contentType: "text/plain",
      bytes: 9,
      headers: {},
      body: "Not found",
      error: null
    },
    {
      kind: "llmsFull",
      label: "/llms-full.txt",
      path: "/llms-full.txt",
      url: "https://docs.example.com/llms-full.txt",
      status: 200,
      ok: true,
      found: true,
      contentType: "text/plain; charset=utf-8",
      bytes: 128420,
      headers: { "content-type": "text/plain; charset=utf-8" },
      body: "# Example Docs Full\n\nExpanded Markdown corpus...",
      error: null
    },
    {
      kind: "robots",
      label: "/robots.txt",
      path: "/robots.txt",
      url: "https://docs.example.com/robots.txt",
      status: 200,
      ok: true,
      found: true,
      contentType: "text/plain; charset=utf-8",
      bytes: robotsBody.length,
      headers: { "content-type": "text/plain; charset=utf-8" },
      body: robotsBody,
      error: null
    },
    {
      kind: "sitemap",
      label: "/sitemap.xml",
      path: "/sitemap.xml",
      url: "https://docs.example.com/sitemap.xml",
      status: 200,
      ok: true,
      found: true,
      contentType: "application/xml",
      bytes: 21182,
      headers: { "content-type": "application/xml" },
      body: "<urlset><url><loc>https://docs.example.com/getting-started</loc></url><url><loc>https://docs.example.com/api/reference</loc></url></urlset>",
      error: null
    }
  ],
  llms: {
    present: true,
    title: "Example Docs",
    summary: "Authoritative product documentation, API references, and implementation guides for Example Docs.",
    sections: ["Core resources", "Product guides", "Optional"],
    linkCount: 8,
    absoluteLinks: 8,
    relativeLinks: 0,
    duplicateLinks: 0,
    optionalLinks: 1,
    estimatedWords: 47,
    estimatedTokens: 63,
    issues: [
      { level: "good", message: "H1 title is present." },
      { level: "good", message: "Blockquote summary is present." },
      { level: "good", message: "3 content sections found." },
      { level: "good", message: "8 Markdown links found." }
    ],
    links: [
      {
        text: "Getting started",
        href: "https://docs.example.com/getting-started",
        absolute: true,
        duplicate: false,
        section: "Core resources"
      },
      {
        text: "API reference",
        href: "https://docs.example.com/api/reference",
        absolute: true,
        duplicate: false,
        section: "Core resources"
      },
      {
        text: "SDK guides",
        href: "https://docs.example.com/sdks",
        absolute: true,
        duplicate: false,
        section: "Core resources"
      },
      {
        text: "Changelog",
        href: "https://docs.example.com/changelog",
        absolute: true,
        duplicate: false,
        section: "Core resources"
      }
    ],
    score: 86
  },
  policies: [
    {
      agent: "OAI-SearchBot",
      intent: "search",
      status: "allowed",
      source: "OAI-SearchBot",
      note: "OpenAI search visibility crawler"
    },
    {
      agent: "GPTBot",
      intent: "training",
      status: "blocked",
      source: "GPTBot",
      note: "OpenAI training crawler control"
    },
    {
      agent: "ClaudeBot",
      intent: "training",
      status: "blocked",
      source: "ClaudeBot",
      note: "Anthropic model-development crawler"
    },
    {
      agent: "Claude-SearchBot",
      intent: "search",
      status: "allowed",
      source: "Claude-SearchBot",
      note: "Anthropic search result crawler"
    },
    {
      agent: "Claude-User",
      intent: "user-fetch",
      status: "unspecified",
      source: "none",
      note: "Anthropic user-request fetcher with no matching rule"
    },
    {
      agent: "PerplexityBot",
      intent: "search",
      status: "allowed",
      source: "PerplexityBot",
      note: "Perplexity search crawler"
    },
    {
      agent: "Perplexity-User",
      intent: "user-fetch",
      status: "unspecified",
      source: "none",
      note: "Perplexity user-request fetcher with no matching rule"
    },
    {
      agent: "Google-Extended",
      intent: "training",
      status: "blocked",
      source: "Google-Extended",
      note: "Google Gemini training and grounding control token"
    },
    {
      agent: "Googlebot",
      intent: "search-core",
      status: "allowed",
      source: "*",
      note: "Google Search crawler baseline using wildcard rules"
    }
  ],
  sitemap: {
    present: true,
    urlCount: 2,
    isIndex: false
  },
  headers: {
    linkHeader: '<https://docs.example.com/llms.txt>; rel="llms"',
    xLlmsTxt: "",
    advertisedLlms: ["https://docs.example.com/llms.txt"]
  },
  recommendations: [
    "Add explicit user-request crawler rules so policy intent is easier to audit.",
    "Keep the llms.txt link list curated; avoid turning it into a raw sitemap clone.",
    "Review training-related crawler settings with legal and content owners."
  ]
};
